import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Input, InputNumber, Select } from 'antd';
import { FilterBar } from '../../../components/list';
import { useOfferingCategoryOptions } from '../../../hooks/options/useOfferingCategoryOptions';
import type { OfferingStatus } from '../types';
import { useDebounce } from '../../../hooks/useDebounce';

const { Search } = Input;

export interface OfferingFilterValues {
  search?: string;
  status?: OfferingStatus;
  offeringCategoryId?: string;
  hasActiveDiscount?: boolean;
  minBasePrice?: number;
  maxBasePrice?: number;
}

export interface OfferingFilterBarProps {
  value: OfferingFilterValues;
  onChange: (value: OfferingFilterValues) => void;
  onReset: () => void;
}

const statusOptions: { value: OfferingStatus; label: string }[] = [
  { value: 0, label: 'Active' },
  { value: 1, label: 'Deactivated' },
];

const discountOptions = [
  { value: 'true', label: 'With active discount' },
  { value: 'false', label: 'Without active discount' },
];

const OfferingFilterBar: React.FC<OfferingFilterBarProps> = ({
  value,
  onChange,
  onReset,
}) => {
  const { data: categories = [] } = useOfferingCategoryOptions({ activeOnly: true });

  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useLayoutEffect(() => { valueRef.current = value; }, [value]);
  useLayoutEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  const lastCommittedRef = useRef(value.search ?? '');

  const [localSearch, setLocalSearch] = useState(value.search ?? '');
  const debouncedSearch = useDebounce(localSearch, 300);

  // Sync debounced value to parent (passive debounce — fires ~300ms after typing stops)
  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (trimmed === lastCommittedRef.current) return;
    lastCommittedRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, search: trimmed || undefined });
  }, [debouncedSearch]);

  // Sync from parent (e.g., external reset)
  useEffect(() => {
    setLocalSearch(value.search ?? '');
  }, [value.search]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalSearch(val);
    if (val === '') {
      // Clear (X) button — fire immediately
      if (lastCommittedRef.current === '') return;
      lastCommittedRef.current = '';
      onChangeRef.current({ ...valueRef.current, search: undefined });
    }
  };

  const handleSearch = (v: string) => {
    // Enter key or search button — fire immediately (no debounce)
    const trimmed = v.trim();
    setLocalSearch(v);
    if (trimmed === lastCommittedRef.current) return;
    lastCommittedRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, search: trimmed || undefined });
  };

  const handleStatusChange = (v: OfferingStatus | undefined) => {
    onChange({ ...value, status: v });
  };
  const handleCategoryChange = (v: string | undefined) => {
    onChange({ ...value, offeringCategoryId: v });
  };
  const handleDiscountChange = (v: string | undefined) => {
    const hasActiveDiscount = v === 'true' ? true : v === 'false' ? false : undefined;
    onChange({ ...value, hasActiveDiscount });
  };
  const handleMinPrice = (v: number | null) => {
    onChange({ ...value, minBasePrice: v ?? undefined });
  };
  const handleMaxPrice = (v: number | null) => {
    onChange({ ...value, maxBasePrice: v ?? undefined });
  };

  const hasActiveFilters =
    Boolean(value.search) ||
    value.status !== undefined ||
    value.offeringCategoryId !== undefined ||
    value.hasActiveDiscount !== undefined ||
    value.minBasePrice !== undefined ||
    value.maxBasePrice !== undefined;

  const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' };
  const box: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2 };

  return (
    <FilterBar onReset={hasActiveFilters ? onReset : undefined}>
      <div style={box}>
        <span style={lbl}>Search</span>
        <Search
          allowClear
          onSearch={handleSearch}
          onChange={handleSearchChange}
          value={localSearch}
          style={{ width: 240 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Status</span>
        <Select<OfferingStatus>
          allowClear
          options={statusOptions}
          value={value.status}
          onChange={handleStatusChange}
          style={{ width: 140 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Category</span>
        <Select<string>
          allowClear
          options={categories.map((c) => ({
            value: c.id,
            label: c.label,
          }))}
          value={value.offeringCategoryId}
          onChange={handleCategoryChange}
          style={{ width: 180 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Discount</span>
        <Select<string>
          allowClear
          options={discountOptions}
          value={value.hasActiveDiscount === true ? 'true' : value.hasActiveDiscount === false ? 'false' : undefined}
          onChange={handleDiscountChange}
          style={{ width: 180 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Min Price</span>
        <InputNumber
          min={0}
          value={value.minBasePrice}
          onChange={handleMinPrice}
          style={{ width: 120 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Max Price</span>
        <InputNumber
          min={0}
          value={value.maxBasePrice}
          onChange={handleMaxPrice}
          style={{ width: 120 }}
        />
      </div>
    </FilterBar>
  );
};

export default OfferingFilterBar;
